import { motion } from "framer-motion";
import { CameraOff } from "lucide-react";
import { BentoCard } from "@/components/BentoCard";
import type { CameraStatus } from "@/hooks/useCamera";

const EASE = [0.22, 1, 0.36, 1] as const;

interface ErrorCopy {
  headline: string;
  copy: string;
  steps?: string[];
}

// Honest, precise messages per camera failure. Never claims to auto-open Android settings.
const COPY: Record<string, ErrorCopy> = {
  blocked_by_overlay: {
    headline: "Camera access unavailable",
    copy: "BuildEase could not access your phone's camera. Your browser may be blocking the permission request, or another app may be interfering with camera access.",
    steps: [
      "Close any floating chat bubbles or screen overlays.",
      "Check that Chrome has camera permission.",
      "Check that BuildEase is allowed to use your camera.",
      "Return to BuildEase and try again.",
    ],
  },
  denied: {
    headline: "Camera permission needed",
    copy: "BuildEase doesn't have permission to use your camera. Allow camera access for this site, then try again.",
    steps: [
      "Open your browser's site settings for BuildEase.",
      "Set Camera to Allow.",
      "Return to BuildEase and try again.",
    ],
  },
  not_found: {
    headline: "No camera found",
    copy: "We couldn't find a compatible camera on this device. You can upload your plan instead.",
  },
  in_use: {
    headline: "Camera is busy",
    copy: "Your camera may be in use by another app. Close other apps using the camera, then try again.",
  },
  security: {
    headline: "Secure camera access unavailable",
    copy: "Camera access needs a secure connection. Open BuildEase from the installed app or a secure browser tab, then try again.",
  },
  unsupported: {
    headline: "Camera not supported",
    copy: "This browser can't access the camera. You can upload your plan instead.",
  },
  unavailable: {
    headline: "Camera unavailable",
    copy: "The camera isn't available in this context right now. You can upload your plan instead.",
  },
  error: {
    headline: "Camera error",
    copy: "Something interrupted camera access. Please try again, or upload your plan instead.",
  },
};

export function CameraErrorState({
  status,
  onRetry,
  onUpload,
}: {
  status: CameraStatus;
  onRetry: () => void;
  onUpload: () => void;
}) {
  const c = COPY[status] ?? COPY.error;
  return (
    <div data-testid="camera-error-state" className="absolute inset-0 z-20 flex items-center justify-center overflow-y-auto p-5">
      <div aria-hidden className="absolute inset-0 bg-obsidian/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative my-auto w-full max-w-md"
      >
        <BentoCard className="flex flex-col items-center gap-5 p-7 text-center sm:p-9">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-cad/30 bg-white/[0.03]">
            <CameraOff size={28} className="text-white/75" />
          </span>
          <div className="flex flex-col gap-2">
            <h2 data-testid="camera-error-headline" className="font-display text-2xl font-semibold tracking-tight text-white">{c.headline}</h2>
            <p className="font-sans text-sm leading-relaxed text-white/60">{c.copy}</p>
          </div>
          {c.steps && (
            <ol className="flex w-full flex-col gap-2.5 text-left">
              {c.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-cad/40 font-mono text-[10px] text-white/60">{i + 1}</span>
                  <span className="font-sans text-sm text-white/70">{s}</span>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-1 flex w-full flex-col gap-3 sm:flex-row">
            <motion.button
              type="button"
              data-testid="camera-error-retry"
              onClick={onRetry}
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.98 }}
              className="flex h-12 flex-1 items-center justify-center rounded-2xl bg-safety font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90"
            >
              Try Again
            </motion.button>
            <motion.button
              type="button"
              data-testid="camera-error-upload"
              onClick={onUpload}
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.98 }}
              className="flex h-12 flex-1 items-center justify-center rounded-2xl border border-cad/60 font-display text-sm font-semibold text-white/85 transition-colors duration-200 hover:border-white/60 hover:text-white"
            >
              Upload Plan Instead
            </motion.button>
          </div>
        </BentoCard>
      </motion.div>
    </div>
  );
}
